import React from 'react';
import { startCase, toLower } from 'lodash';

import FormControl from 'components/FormControl';
import PropertiesSearchInput from 'components/PropertiesSearchInput';
import { SearchInputSize } from 'components/SearchInput/enums';
import OptionSearchFilter from 'components/OptionSearchFilter';
import RemoteChoiceInput from 'components/Inputs/RemoteChoiceInput';
import TextInput from 'components/Inputs/TextInput';
import { NaicsField } from 'components/FindComps/common/NaicsFields/NaicsField';
import {
  I18N_AVANT_PLATFORM_LEASE_PROMPT_PATH,
  I18N_AVANT_PLATFORM_PROPERTY_PROMPT_PATH,
  I18N_AVANT_PROPERTY_COMMON_LABEL_PATH,
  I18N_PLATFORM_COMMON_WORD_PATH,
  I18N_PLATFORM_ROLES_COMPANY_PROMPT_PATH,
} from 'constants/i18n';
import { ColorNames } from 'constants/colorNames';
import { IdName } from 'interfaces/IdName';
import { IProperty } from 'interfaces/IProperty';
import { ICompany } from 'interfaces/ICompany';
import { IPropertyCompany } from 'interfaces/IPropertyCompany';
import { INaics } from 'interfaces/INaics';

import { INDUSTRIES_QUERY } from 'graphql/industries';
import { COMPANY_INVESTOR_TYPES } from 'graphql/company/queries';
import { SEARCH_COMPANIES_PROPERTIES_QUERY } from 'graphql/common';
import { getPropertyAddress } from 'utils/formatters/property';
import { translateText } from 'utils/i18n';

import { CompanyFieldsNames, INDUSTRY_FIELD_VARIABLES } from './constants';
import formsStyles from '../FormSection.module.scss';
import styles from './CompanyForm.module.scss';

interface Props {
  company: ICompany;
  onChange: (field: string, value: any) => void;
}
export const CompanyLocationSection = ({ company, onChange }: Props) => {
  const {
    INDUSTRY,
    HEADQUARTER_PROPERTY,
    HEADQUARTERS,
    INVESTOR,
  } = CompanyFieldsNames;

  const updateHeadQuarterFields = (
    propertyHeadquarter: IProperty | null,
    textHeadquarter?: string | null,
  ) => {
    onChange(HEADQUARTERS, textHeadquarter);
    onChange(HEADQUARTER_PROPERTY, propertyHeadquarter);
  };

  const onNaicsChange = (v: IdName | null, items: INaics[]) => {
    const fullValue = items?.find(n => n.id === v?.id)!;
    delete fullValue?.name;
    onChange(CompanyFieldsNames.NAICS, fullValue ?? null);
    onChange(CompanyFieldsNames.NAICS_ID, v?.id ?? null);
  };

  const getSelected4DititNaicsCode = () => {
    if (!company.naics) return undefined;

    return {
      ...company.naics,
      name: `${company.naics.fourDigitCode} - ${company.naics.fourDigitName}`,
    };
  };

  const getSelected2DititNaicsCode = () => {
    if (!company.naics) return undefined;
    return `${company.naics.twoDigitCode} - ${company.naics.twoDigitName}`;
  };

  return (
    <>
      <FormControl
        label={translateText(
          `${I18N_PLATFORM_COMMON_WORD_PATH}.companyAddress`,
        )}
        wrapperClassName={formsStyles['form-row']}
      >
        <PropertiesSearchInput
          inputId={'company-headquarter-property-input-id'}
          searchInputProps={{ size: SearchInputSize.Small }}
          onClickSuggestion={property => {
            property.id = property.propertyId ?? property.id;
            property.primaryAddress = property.primaryAddress ?? property.name;
            updateHeadQuarterFields(property, null);
          }}
          onChangeText={value => updateHeadQuarterFields(null, value)}
          graphqlParams={{
            query: SEARCH_COMPANIES_PROPERTIES_QUERY,
            field: 'name',
            resultDataName: 'propertiesCompanies',
            onlyProperties: true,
          }}
          property={{
            id: company.headquarterProperty?.id,
            primaryAddress: company.headquarterProperty
              ? getPropertyAddress(company.headquarterProperty) || ''
              : company.headquarters,
            name: company.headquarterProperty?.name,
          }}
          placeholder={translateText(
            `${I18N_AVANT_PLATFORM_PROPERTY_PROMPT_PATH}.enterAddress`,
          )}
          parseResults={(data: IPropertyCompany[]): IPropertyCompany[] => {
            return data.map(propertyCompany => ({
              ...propertyCompany,
              name: propertyCompany.displayAddress || propertyCompany.name,
            }));
          }}
          onClearSelectedSuggestion={() => updateHeadQuarterFields(null, null)}
          withMarketSwitch={false}
          customOption={{
            onSelection: value => updateHeadQuarterFields(null, value),
          }}
        />
      </FormControl>
      <FormControl
        label={translateText(
          `${I18N_PLATFORM_COMMON_WORD_PATH}.companyIndustry`,
        )}
        wrapperClassName={formsStyles['form-row']}
      >
        <OptionSearchFilter
          small
          hasBorder
          noMarginBottom
          sortAlphabetically
          gridColumnsCount={4}
          isMultipleSelect={false}
          query={INDUSTRIES_QUERY}
          selectedItem={company[INDUSTRY]}
          label={translateText(
            `${I18N_AVANT_PROPERTY_COMMON_LABEL_PATH}.selectIndustry`,
          )}
          searchPlaceholder={startCase(
            toLower(
              translateText(
                `${I18N_AVANT_PLATFORM_LEASE_PROMPT_PATH}.searchIndustry`,
              ),
            ),
          )}
          variables={INDUSTRY_FIELD_VARIABLES}
          resultBarClassName={styles['industry-field']}
          backgroundContainerColor={ColorNames.ayWhiteColor}
          onChange={(value?: IdName[]) => onChange(INDUSTRY, value)}
          queryParser={data =>
            data?.industries?.filter((item: IdName) => item.name)
          }
        />
      </FormControl>
      <FormControl
        label={translateText(
          `${I18N_PLATFORM_COMMON_WORD_PATH}.2digitsNaicsCode`,
        )}
        wrapperClassName={formsStyles['form-row']}
      >
        <TextInput
          borderBox
          fullWidth
          isDisabled
          id="2-digit-naics-code"
          value={getSelected2DititNaicsCode()}
        />
      </FormControl>
      <NaicsField
        usage="findComps"
        isMultipleSelect={false}
        rowLabel={translateText(
          `${I18N_PLATFORM_COMMON_WORD_PATH}.4digitsNaicsCode`,
        )}
        label={translateText(
          `${I18N_PLATFORM_ROLES_COMPANY_PROMPT_PATH}.select4DigitNaicsCode`,
        )}
        selectedItem={getSelected4DititNaicsCode()}
        onChange={(v, data) => {
          onNaicsChange(v?.[0] ?? null, data);
        }}
      />

      <FormControl
        label={translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.investorType`)}
        wrapperClassName={formsStyles['form-row']}
        isMultiline
      >
        <RemoteChoiceInput
          query={COMPANY_INVESTOR_TYPES}
          queryParser={data => data?.investorTypes || []}
          selectedItem={{ id: company.investorType?.id }}
          onChange={(value?: IdName) => onChange(INVESTOR, value)}
        />
      </FormControl>
    </>
  );
};
